// Firma yetkilisi: onay durumu paneli, onay bekleme uyarısı
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Clock, Hourglass, CheckCircle2, XCircle, ChevronRight, ChevronLeft, Building2, PartyPopper, Plus } from 'lucide-react';
import { Button } from '../ui/button';

import { FirmDocuments, ResubmitButton } from './FirmDocuments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FIRM_ROLE = 'firma_yetkilisi';

export const REG_STATUS = {
  pending: { label: 'Onay Bekliyor', icon: Hourglass, cls: 'bg-amber-50 border-amber-200 text-amber-800', dot: 'bg-amber-500' },
  approved: { label: 'Onaylandı', icon: CheckCircle2, cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', dot: 'bg-emerald-500' },
  rejected: { label: 'Reddedildi', icon: XCircle, cls: 'bg-rose-50 border-rose-200 text-rose-800', dot: 'bg-rose-500' },
};

export const useMyCompany = (enabled) => {
  const [company, setCompany] = useState(null);
  const load = useCallback(() => {
    if (!enabled) return;
    axios.get(`${API}/my-company`).then(res => setCompany(res.data)).catch(() => setCompany(null));
  }, [enabled]);
  // Durum değişikliği anlık bildirimle gelir (company_status); yedek kontrol 5 dk, arka planda istek yok, sekmeye dönünce tazelenir
  useEffect(() => {
    load();
    if (!enabled) return undefined;
    const t = setInterval(() => { if (!document.hidden) load(); }, 300000);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [load, enabled]);
  return [company, load];
};

const Row = ({ label, value }) => (
  <div className="text-xs"><p className="text-slate-400">{label}</p><p className="text-slate-700 font-medium break-words">{value || '-'}</p></div>
);

export const FirstPermitButton = ({ hasPermits, onClick, locked, testId = 'firm-first-permit-btn' }) => {
  const navigate = useNavigate();
  return (
    <Button size="sm" disabled={locked} onClick={() => { onClick?.(); navigate('/work-permits/new'); }} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid={testId}>
      <Plus className="h-3.5 w-3.5 mr-1" />{hasPermits ? 'Yeni Çalışma İzni Talebi' : 'İlk Çalışma İzni Talebini Oluştur'}
    </Button>
  );
};

export const ApprovedCelebrationDialog = ({ open, onClose, companyName }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
    <AlertDialogContent data-testid="firm-approved-dialog" data-allow-pending>
      <AlertDialogHeader>
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-2"><PartyPopper className="h-7 w-7 text-emerald-600" /></div>
        <AlertDialogTitle className="text-center">Tebrikler, firmanız onaylandı!</AlertDialogTitle>
        <AlertDialogDescription className="text-center">
          {companyName} kaydı İSG birimi tarafından onaylandı. Artık personel ekleyebilir ve çalışma izni talebi oluşturabilirsiniz.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div className="space-y-2">
        <FirstPermitButton hasPermits={false} onClick={onClose} testId="firm-approved-dialog-permit-btn" />
        <Button variant="ghost" className="w-full" onClick={onClose} data-testid="firm-approved-dialog-close">Daha Sonra</Button>
      </div>
    </AlertDialogContent>
  </AlertDialog>
);

export const FirmStatusContent = ({ company, onChanged, permitCount }) => {
  const st = REG_STATUS[company?.registration_status] || REG_STATUS.pending;
  return (
    <>
      <div className={`rounded-lg border p-4 ${st.cls}`} data-testid="firm-status-badge">
        <div className="flex items-center gap-2 font-semibold"><st.icon className="h-5 w-5" />{st.label}</div>
        {company?.registration_status === 'pending' && <p className="text-xs mt-2">Kaydınız İSG birimi tarafından inceleniyor. Onaylandığında işlem yapabileceksiniz.</p>}
        {company?.registration_status === 'rejected' && company.registration_reason && <p className="text-xs mt-2" data-testid="firm-status-reason">Sebep: {company.registration_reason}</p>}
        {company?.registration_status === 'approved' && <p className="text-xs mt-2">Firmanız onaylandı. Personel ekleyebilir ve çalışma izni talebi oluşturabilirsiniz.</p>}
      </div>
      {company?.registration_status === 'approved' && <FirstPermitButton hasPermits={permitCount > 0} locked={company.kvkk_renewal_required} />}
      {company?.registration_status === 'rejected' && <ResubmitButton company={company} onChanged={onChanged} />}
      {company && <FirmDocuments company={company} onChanged={onChanged} />}
      {company && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Building2 className="h-4 w-4" />{company.name}</div>
          <Row label="Ticari Unvan" value={company.trade_name} />
          <Row label="SGK Sicil No" value={company.sgk_sicil_no} />
          <Row label="NACE Kodu" value={company.nace_kodu} />
          <Row label="Faaliyet / İş Tanımı" value={company.activity_description} />
          <Row label="Yetkili" value={`${company.contact_name || ''} ${company.contact_surname || ''}`} />
          <Row label="Telefon" value={company.contact_phone} />
          <Row label="E-posta" value={company.contact_email} />
          <Row label="Kayıt Tarihi" value={company.registered_at?.slice(0, 10).split('-').reverse().join('.')} />
        </div>
      )}
    </>
  );
};

export const FirmStatusPanel = ({ company, open, onToggle, onChanged, permitCount }) => {
  const st = REG_STATUS[company?.registration_status] || REG_STATUS.pending;
  if (!open) {
    return (
      <button onClick={onToggle} className="hidden lg:flex fixed right-0 top-24 z-30 items-center gap-1 rounded-l-lg border border-r-0 bg-white px-2 py-3 shadow-sm text-xs font-medium text-slate-600" data-testid="firm-status-panel-open">
        <ChevronLeft className="h-4 w-4" /><span className={`w-2 h-2 rounded-full ${st.dot}`} />
      </button>
    );
  }
  return (
    <aside className="hidden lg:block fixed right-0 inset-y-0 z-30 w-72 bg-white border-l border-slate-200 overflow-y-auto" data-testid="firm-status-panel" data-allow-pending>
      <div className="p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-800">Firma Onay Durumu</h2>
          <button onClick={onToggle} className="text-slate-400 hover:text-slate-700" aria-label="Paneli kapat" data-testid="firm-status-panel-close"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <FirmStatusContent company={company} onChanged={onChanged} permitCount={permitCount} />
      </div>
    </aside>
  );
};

export const PendingNoticeDialog = ({ open, onClose }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
    <AlertDialogContent data-testid="firm-pending-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-amber-500" />Firmanız onay beklemektedir</AlertDialogTitle>
        <AlertDialogDescription>
          Kayıt bilgileriniz İSG biriminin onayına gönderildi. Onaylanana kadar menüleri gezebilirsiniz ancak herhangi bir işlem yapamazsınız.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={onClose} data-testid="firm-pending-ok-btn">Tamam</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"], [role="combobox"], [role="checkbox"], [role="tab"], [role="menuitem"]';

// Onay bekleyen firmada içerik alanındaki her etkileşimi engeller ve uyarı gösterir
export const useBlockWhilePending = (blocked, onBlocked) => (e) => {
  if (!blocked) return;
  const el = e.target.closest(INTERACTIVE);
  if (!el || el.closest('[data-allow-pending]')) return;
  e.preventDefault();
  e.stopPropagation();
  onBlocked();
};

export const PendingBanner = ({ status }) => {
  if (status === 'approved') return null;
  const st = REG_STATUS[status] || REG_STATUS.pending;
  return (
    <div className={`mb-6 rounded-lg border px-4 py-3 text-sm flex items-center gap-2 ${st.cls}`} data-testid="firm-pending-banner" data-allow-pending>
      <st.icon className="h-4 w-4 shrink-0" />
      {status === 'rejected' ? 'Firma kaydınız reddedildi. Detaylar sağdaki panelde.' : 'Firmanız onay beklemektedir. Onaylanana kadar işlem yapamazsınız.'}
    </div>
  );
};
